import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CircularProgress,
  Typography,
  Button,
} from '@mui/material';
import ThemeBackground from '../Layout/ThemeBackground';
import api from '../../utils/api';
import ReviewApplicationForm from './ReviewApplicationForm';
import { Counsellor } from '../../types/counsellor';

const ReviewApplicationFormContainer: React.FC = () => {
  const { counsellorId } = useParams<{ counsellorId: string }>();
  const navigate = useNavigate();
  const [counsellor, setCounsellor] = useState<Counsellor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!counsellorId) {
          setError('No counsellor ID provided');
          setLoading(false);
          return;
        }

        const counsellorResponse = await api.get(`/api/counsellors/${counsellorId}`);
        console.log('Fetched counsellor data:', counsellorResponse.data);
        setCounsellor(counsellorResponse.data);
        setLoading(false);
      } catch (error: any) {
        console.error('Error fetching counsellor details:', error);
        setError(error.response?.data?.message || 'Error fetching counsellor details');
        setLoading(false);
      }
    };

    fetchData();
  }, [counsellorId]);

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8 overflow-y-scroll">
          <div className="glass-card p-6 min-h-[calc(100vh-4rem)] flex items-center justify-center">
            <CircularProgress />
          </div>
        </div>
      </ThemeBackground>
    );
  }

  if (error || !counsellor) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8 overflow-y-scroll">
          <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
            <Typography className="text-red-400">{error || 'Failed to load counsellor details'}</Typography>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                  <Button
            variant="contained"
            onClick={() => navigate(`/counsellors/${counsellorId}`)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '160px',
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Back to Counsellor Details
          </Button>
                  </div>
                </div></div>


          {/* Title */}
          <div className="text-right">
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 800,
                fontSize: '2.5rem',
                color: '#1a365d',
                letterSpacing: '0.5px'
              }}
            >
              Review Application
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{
                color: '#4a5568',
                fontWeight: 500,
                fontSize: '1.1rem',
                mb: 2
              }}
            >
              {counsellor.firstName} {counsellor.lastName}
            </Typography>
          </div>
        </div></div>

        {/* Application Form */}
        <ReviewApplicationForm 
          applicationData={counsellor}
        />
      </div>
    </div>
    </div>
    </ThemeBackground>
  );
};

export default ReviewApplicationFormContainer;
